import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { fetchConnections } from "../../redux/actions/connections";
import OtherFollowers from './OtherFollowers';
import OtherFollowing from './OtherFollowing';
import Friend from '../Friend';
import { server } from '../../server';

const normalizeConnection = (connection = {}) => ({
  ...connection,
  _id: connection._id || connection.id,
  name: connection.name || connection.shopName || connection.handle || 'MATRIX member',
  avatar: connection.avatar?.url || connection.avatar || connection.profileImage || '/default-avatar.png',
  type: connection.type || connection.profileType || connection.connectionType || 'User',
});

const filterConnections = (connections = [], searchTerm = '') => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const normalizedConnections = connections.map(normalizeConnection).filter((connection) => connection._id);

  if (!normalizedSearch) {
    return normalizedConnections;
  }

  return normalizedConnections.filter((connection) => (
    String(connection.name || '').toLowerCase().includes(normalizedSearch)
    || String(connection.handle || '').toLowerCase().includes(normalizedSearch)
  ));
};

const getConnectionIds = (...connectionLists) => new Set(
  connectionLists
    .flat()
    .map((connection) => String(connection?._id || connection?.id || ''))
    .filter(Boolean)
);

const ConnectionSearchSection = ({ label, connections, emptyText }) => (
  <section className="connections-widget-search-section">
    <span className="connections-widget-search-section-label">{label}</span>
    {connections.length > 0 ? (
      <div className="connections-widget-search-results-list">
        {connections.map((connection) => (
          <div className="connections-widget-search-result" key={`${label}-${connection._id}`}>
            <Friend
              friendId={connection._id}
              name={connection.name}
              subtitle={connection.handle ? `@${connection.handle}` : connection.type}
              avatar={connection.avatar}
              friendType={connection.type}
            />
          </div>
        ))}
      </div>
    ) : (
      <p className="connections-widget-search-empty">{emptyText}</p>
    )}
  </section>
);

export default function OtherConnectionsWidget({ userId, profileType }) {
  const dispatch = useDispatch();

  // Extract followers and following state along with their loading states and fetched flags
  const {
    followers,
    following,
    isLoadingFollowers,
    isLoadingFollowing,
    hasFetchedFollowers,
    hasFetchedFollowing,
    error,
  } = useSelector((state) => state.connections);
  const [recommendedConnections, setRecommendedConnections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch the connections when the component mounts or when userId/profileType changes
  useEffect(() => {
    if (userId && profileType) {
      if (!hasFetchedFollowers) {
        dispatch(fetchConnections(userId, profileType, 'followers'));
      }
      if (!hasFetchedFollowing) {
        dispatch(fetchConnections(userId, profileType, 'following'));
      }
    }
  }, [dispatch, userId, profileType, hasFetchedFollowers, hasFetchedFollowing]);

  useEffect(() => {
    if (!userId || !profileType) {
      setRecommendedConnections([]);
      return;
    }

    let isMounted = true;

    const fetchRecommendedConnections = async () => {
      try {
        const response = await axios.get(
          `${server}/connections/recommend-connections/${userId}/${profileType}`,
          { withCredentials: true }
        );

        if (isMounted) {
          setRecommendedConnections(Array.isArray(response.data.recommendedConnections) ? response.data.recommendedConnections : []);
        }
      } catch (error) {
        if (isMounted) {
          setRecommendedConnections([]);
        }
      }
    };

    fetchRecommendedConnections();

    return () => {
      isMounted = false;
    };
  }, [userId, profileType]);

  // State for the active tab (followers or following)
  const [activeTab, setActiveTab] = useState('Followers');
  const tabs = [
    { name: 'Followers', current: activeTab === 'Followers' },
    { name: 'Following', current: activeTab === 'Following' },
    { name: 'Search', current: activeTab === 'Search' },
  ];

  const connectedIds = useMemo(() => getConnectionIds(followers, following, [{ _id: userId }]), [followers, following, userId]);
  const searchFollowers = useMemo(() => filterConnections(followers, searchTerm), [followers, searchTerm]);
  const searchFollowing = useMemo(() => filterConnections(following, searchTerm), [following, searchTerm]);
  const communityMatches = useMemo(() => filterConnections(recommendedConnections, searchTerm)
    .filter((connection) => !connectedIds.has(String(connection._id))), [connectedIds, recommendedConnections, searchTerm]);

  return (
    <div className="connections-widget">
      <div className="connections-widget-container">
        <div className="connections-widget-header">
          <h1>Social Connections</h1>
        </div>
        <div className="connections-widget-tabs">
          <nav aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.name}
                className={`connections-widget-tab ${tab.current ? 'active' : ''}`}
                aria-current={tab.current ? 'page' : undefined}
                onClick={() => setActiveTab(tab.name)}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
        {activeTab === 'Followers' ? (
          <OtherFollowers
            key={userId}
            followers={followers}
            isLoading={isLoadingFollowers}
            error={error}
          />
        ) : null}
        {activeTab === 'Following' ? (
          <OtherFollowing
            key={userId}
            following={following}
            isLoading={isLoadingFollowing}
            error={error}
          />
        ) : null}
        {activeTab === 'Search' ? (
          <div className="connections-widget-search-panel">
            <input
              type="text"
              className="connections-widget-search-input"
              placeholder="Search followers, following, and community"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <ConnectionSearchSection
              label="Following"
              connections={searchFollowing}
              emptyText="No following matches."
            />
            <ConnectionSearchSection
              label="Followers"
              connections={searchFollowers}
              emptyText="No follower matches."
            />
            <ConnectionSearchSection
              label="Community"
              connections={communityMatches}
              emptyText={searchTerm.trim() ? 'No community matches yet.' : 'Search to discover more community profiles.'}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}


